import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { listTaxbills } from '../../modules/taxIssuedModule/taxbills';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import TaxbillList from '../../components/taxIssuedmng/TaxbillList';
import { TaxbillListBox } from '../../components/taxIssuedmng/TaxbillListBox';
import TaxbillStatusList from '../../components/taxIssuedmng/TaxbillStatusList';
import { getDefaultSearchRange } from '../../utils/defaultSearchRange';

const TaxbillListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  // 검색 시작일/종료일을 비워두지 않고 오늘 기준 앞뒤 1년을 기본 조회범위로 둔다.
  const defaultRange = getDefaultSearchRange();
  const [searchBillTyp, setsearchBillTyp] = useState('');
  const [searchTyp, setSearchTyp] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('invNo');
  const [searchWords, setSearchWords] = useState('');
  const [searchYn, setSearchYn] = useState('Y');
  const [searchStrtDt, setSearchStrtDt] = useState(defaultRange.searchStrtDt);
  const [searchEndDt, setSearchEndDt] = useState(defaultRange.searchEndDt);

  const { taxbills, error, loading } = useSelector(
    ({ taxIssuedModule, loading }) => ({
      taxbills: taxIssuedModule.taxbills.taxbills,
      error: taxIssuedModule.taxbills.error,
      loading: loading['taxIssued/LIST_TAXBILLS'],
    }),
  );

  const searchList = (values) => {
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      billTyp: searchBillTyp,
      searchTyp: searchTyp,
      reqCompYn: searchYn,
      strtDt: startDate,
      endDt: endDate,
      [values.searchKeyword]: values.searchWords,
    };

    setSearchKeyword(values.searchKeyword);
    setSearchWords(values.searchWords);
    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    dispatch(listTaxbills(searchData));
  };

  const [sortModel, setSortModel] = React.useState([
    {
      field: 'reqCompDt',
      sort: 'desc',
    },
  ]);

  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },

      {
        field: 'invNo',
        headerName: '계산서번호',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/taxIssuedmng/${params.row.invUuid}`}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'billTyp',
        headerName: '유형',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrHq',
        headerName: '본부',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrDpt',
        headerName: '부서',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '요청자',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'reqCompDt',
        headerName: '발행일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
    searchKeyword: searchKeyword,
    searchWords: searchWords,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    searchList(formik.values);
  }, [searchBillTyp, searchTyp, searchYn]);

  const onChangeBillTyp = (billTyp) => {
    if (billTyp === searchBillTyp) {
      billTyp = '';
    }

    setsearchBillTyp(billTyp);
  };

  const onChangeYn = (useYn) => {
    if (useYn === searchBillTyp) {
      useYn = '';
    }

    setSearchYn(useYn);
  };

  const toggleSearchTyp = () => {
    setSearchTyp(!searchTyp);
  };

  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/세금계산서 발급 관리/계산서 리스트"
        icon="taxbill"
      />
      <TaxbillListBox formik={formik} />
      <TaxbillStatusList
        searchBillTyp={searchBillTyp}
        onChangeBillTyp={onChangeBillTyp}
        searchYn={searchYn}
        onChangeYn={onChangeYn}
        searchTyp={searchTyp}
        toggleSearchTyp={toggleSearchTyp}
      />
      <TaxbillList
        columns={columns}
        rows={taxbills}
        error={error}
        loading={loading}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        columnVisibilityModel={{
          id: false,
          invUuid: false,
          roUuid: false,
          billUuid: false,
          prjUuid: false,
          invYn: false,
        }}

      />
    </>
  );
};

export default TaxbillListContainer;
